import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchMyProfile } from '@/lib/supabaseHelpers';
import { supabase } from '@/integrations/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

interface LiveUser {
  user_id: string;
  display_name: string;
  online_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  liveCount: number;
  liveUsers: LiveUser[];
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  liveCount: 0,
  liveUsers: [],
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [liveCount, setLiveCount] = useState(0);
  const [liveUsers, setLiveUsers] = useState<LiveUser[]>([]);

  useEffect(() => {
    let mounted = true;
    const finishLoading = () => {
      if (mounted) setLoading(false);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      finishLoading();
    });

    const loadingTimeout = window.setTimeout(() => {
      finishLoading();
    }, 8000);

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;
        setSession(session);
        setUser(session?.user ?? null);
      })
      .catch((error) => {
        console.error('Initial session fetch failed:', error);
      })
      .finally(() => {
        finishLoading();
      });

    return () => {
      mounted = false;
      window.clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setLiveCount(0);
      setLiveUsers([]);
      return;
    }

    // Record daily active user ping (idempotent via unique constraint)
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const key = `isc_dau_ping_${user.id}_${today}`;
        if (typeof window !== 'undefined' && !localStorage.getItem(key)) {
          await supabase
            .from('user_activity')
            .insert({ user_id: user.id, activity_date: today } as any);
          localStorage.setItem(key, '1');
        }
      } catch { /* ignore duplicate/insert errors */ }
    })();

    // Time-spend heartbeat: send elapsed seconds every 60s while the tab is visible
    let lastTick = Date.now();
    const heartbeat = window.setInterval(() => {
      const now = Date.now();
      const elapsed = Math.round((now - lastTick) / 1000);
      lastTick = now;
      if (document.visibilityState !== 'visible' || elapsed <= 0) return;
      const secs = Math.min(elapsed, 300);
      supabase.rpc('record_activity_seconds' as any, { _seconds: secs }).then(() => {});
    }, 60_000);
    const onVisibility = () => { lastTick = Date.now(); };
    document.addEventListener('visibilitychange', onVisibility);

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let disposed = false;
    // Guards against concurrent setup runs (token refresh re-runs this effect
    // while the previous setup is still awaiting the profile fetch). Without
    // this, supabase.channel() can return the already-subscribed channel and
    // adding callbacks after subscribe() throws.
    let setupInFlight: Promise<void> | null = null;

    const setupPresence = async () => {
      if (setupInFlight) { try { await setupInFlight; } catch { /* fall through */ } }
      if (disposed) return;
      const run = (async () => {
        const email = user.email || '';
        let displayName = '';
        try {
          const profile = await fetchMyProfile(user.id);
          if (profile) {
            displayName = (profile.display_name?.trim() || profile.full_name?.trim()) ?? '';
          }
        } catch { /* fallback below */ }
        if (disposed) return;
        if (!displayName) {
          displayName = user.user_metadata?.display_name?.trim() || user.user_metadata?.full_name?.trim() || '';
        }
        if (!displayName) {
          displayName = email || 'User';
        }

        // Reuse an existing channel for this topic instead of re-adding
        // callbacks — supabase.channel() returns the same instance for a
        // repeated topic, and .on() after .subscribe() throws.
        const existing = supabase.getChannels().find(c => c.topic === 'realtime:global-presence');
        if (existing) {
          channel = existing as typeof channel;
          try {
            await channel.track({
              user_id: user.id,
              display_name: displayName,
              online_at: new Date().toISOString(),
            });
          } catch { /* not subscribed yet — handlers already registered */ }
          return;
        }

        channel = supabase.channel('global-presence', {
          config: { presence: { key: user.id } },
        });

        const syncPresence = () => {
          if (!channel) return;
          const state = channel.presenceState();
          const keys = Object.keys(state);
          setLiveCount(keys.length);
          const users: LiveUser[] = keys.map(key => {
            const presences = state[key] as any[];
            const p = presences[0] || {};
            return {
              user_id: key,
              display_name: p.display_name || 'User',
              online_at: p.online_at || '',
            };
          });
          setLiveUsers(users);
        };

        channel
          .on('presence', { event: 'sync' }, syncPresence)
          .on('presence', { event: 'join' }, syncPresence)
          .on('presence', { event: 'leave' }, syncPresence)
          .subscribe(async (status) => {
            if (status === 'SUBSCRIBED' && !disposed) {
              await channel!.track({
                user_id: user.id,
                display_name: displayName,
                online_at: new Date().toISOString(),
              });
            }
          });
      })();
      setupInFlight = run;
      await run;
    };

    setupPresence();

    return () => {
      window.clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      setLiveCount(0);
      setLiveUsers([]);
      if (channel) supabase.removeChannel(channel);
    };
  }, [user]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, liveCount, liveUsers, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}